import dayjs from 'dayjs'
import { AiFillWarning } from 'react-icons/ai'
import { IoCloseOutline } from 'react-icons/io5'
import { MdOutlineContentCopy } from 'react-icons/md'
import {
  createNotification,
  getContentForType,
  type NotificationItem,
  NotificationStatus,
  NotificationType,
} from '@/utils/notificationUtils'

export interface NotificationItemProps {
  item: NotificationItem
  onNotificationClose: () => void
}

const getStatusAccentClass = (type: NotificationStatus) => {
  switch (type) {
    case NotificationStatus.SUCCESS:
      return 'bg-[#1F9D84] dark:bg-success'
    case NotificationStatus.WARNING:
      return 'bg-warning'
    case NotificationStatus.INFO:
      return 'bg-info'
    default:
      return 'bg-[#C93F4B] dark:bg-danger'
  }
}

const getStatusDotClass = (type: NotificationStatus) => {
  switch (type) {
    case NotificationStatus.SUCCESS:
      return 'bg-[#1F9D84] dark:bg-success'
    case NotificationStatus.WARNING:
      return 'bg-warning'
    case NotificationStatus.INFO:
      return 'bg-info'
    default:
      return 'bg-[#C93F4B] dark:bg-danger'
  }
}

export const NotificationItemComponent = ({ item, onNotificationClose }: NotificationItemProps) => {
  const isLiquidation = item.notificationType === NotificationType.ORDER_LIQUIDATION
  const isPositionClosed = item.notificationType === NotificationType.POSITION_CLOSED
  const date = dayjs(item.timestamp).format(
    isPositionClosed ? 'MMM D, YYYY — HH:mm:ss' : 'MMMM D, YYYY - HH:mm:ss',
  )
  const statusAccentClass = getStatusAccentClass(item.notificationStatus)
  const statusDotClass = getStatusDotClass(item.notificationStatus)

  const onNotificationCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(item.params)).finally()
    createNotification(
      'Copied to clipboard',
      'Notification content was copied',
      NotificationType.DISPOSABLE,
    )
  }

  return (
    <div
      id={'NotificationItemComponent'}
      className={`relative mt-2 overflow-hidden rounded-md border border-slate-200 bg-white text-slate-800 shadow-[0_14px_36px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-gradient-to-b dark:from-dark-darker/95 dark:to-dark/95 dark:text-white dark:shadow-[0_10px_28px_rgba(0,0,0,0.4)] dark:backdrop-blur-sm ${
        isLiquidation
          ? 'ring-2 ring-[#C93F4B]/55 shadow-[0_0_24px_rgba(201,63,75,0.22)] dark:ring-danger/70 dark:shadow-[0_0_24px_rgba(231,81,90,0.35)]'
          : ''
      }`}
    >
      <div className={`h-[2px] w-full rounded-t-sm ${statusAccentClass}`} />
      <div className={'w-full p-3'}>
        <div className={'w-full'}>
          <div className={'flex flex-row items-start gap-2'}>
            <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${statusDotClass}`} />
            {isLiquidation && (
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#C93F4B]/10 dark:bg-danger/15">
                <AiFillWarning
                  size={14}
                  className="text-[#C93F4B] animate-pulse dark:text-danger"
                />
              </div>
            )}
            <h1
              className={`w-full ${
                isLiquidation
                  ? 'font-bold text-base text-[#C93F4B] uppercase tracking-wide dark:text-danger'
                  : isPositionClosed
                    ? 'font-medium text-sm text-slate-900 tracking-[0.01em] dark:text-white'
                    : 'font-bold text-sm text-slate-900 tracking-[0.01em] dark:text-white'
              }`}
            >
              {item.title}
            </h1>
            {item.notificationType !== NotificationType.DISPOSABLE && (
              <button
                type="button"
                className="shrink-0 rounded-md bg-slate-100 p-1 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-950 dark:bg-transparent dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
                onClick={() => onNotificationCopy()}
                aria-label="Copy notification"
              >
                <MdOutlineContentCopy size={16} />
              </button>
            )}
            <button
              type="button"
              className="shrink-0 rounded-md bg-slate-100 p-1 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-950 dark:bg-transparent dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
              onClick={() => onNotificationClose()}
              aria-label="Close notification"
            >
              <IoCloseOutline size={16} />
            </button>
          </div>

          <div className="mt-2 text-sm text-slate-800 [&_.text-white]:!text-slate-900 [&_[class~='text-white/55']]:!text-slate-500 [&_[class~='text-white/70']]:!text-slate-600 dark:text-white-dark dark:[&_.text-white]:!text-white dark:[&_[class~='text-white/55']]:!text-white/55 dark:[&_[class~='text-white/70']]:!text-white/70">
            {getContentForType(item.notificationType, item.params)}
          </div>

          <div className="pt-2 text-right text-[11px] uppercase tracking-[0.08em] text-slate-500 dark:text-white/45">
            {date}
          </div>
        </div>
      </div>
    </div>
  )
}
